import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DEPTH,
    PRICE_TYPE,
    TRADE_TYPE,
    TRADE_TYPE_LANG_MAP,
    ORDER_STATUS_LANG_MAP,
    ORDER_TYPE,
} from 'src/common/constants';
import { contractNameFormatter, currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { Button } from 'src/components/core/button';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { cancelConditionalOrder } from 'src/pages/derivatives/_redux/derivativePageActions';
import { ConditionalOrder, FutureSymbol, OrderTableContentProps } from 'src/types';

const ConditionalOrdersTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterConditionalOrders, setFilterConditionalOrders] = useState(
        [] as Array<ConditionalOrder>,
    );
    const currentSymbol: FutureSymbol = useAppSelector(
        (state) => state.derivativePage.currentSymbol,
    );
    const symbolId = currentSymbol.symbolId;
    const conditionalOrders: Array<ConditionalOrder> = useAppSelector(
        (state) => state.derivativePage.conditionalOrders,
    );
    const slowBroker = useAppSelector((state) => state.socket.slowBroker);
    const futureSymbols = useAppSelector((state) => state.globalData.configV2.futuresSymbol);
    const dispatch = useAppDispatch();

    useEffect(() => {
        let filtered = isShowAll
            ? conditionalOrders
            : conditionalOrders.filter(
                  (conditionalOrder) => conditionalOrder.symbolId === symbolId,
              );
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterConditionalOrders(filtered);
    }, [conditionalOrders, symbolId, isShowAll]);

    const cancelOrder = (orderId: string, type: string) => {
        dispatch(
            cancelConditionalOrder({
                order_id: orderId,
                client_order_id: new Date().getTime(),
                type: type,
            }),
        );
    };

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            width: '8%',
            render: (value: any, row: { [x: string]: any }) => {
                return (
                    <div
                        className={classNames(
                            'contract',
                            {
                                borderGreen:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                borderRed:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {contractNameFormatter(row['symbolName'])}
                    </div>
                );
            },
        },
        {
            title: t('qty'),
            key: 'qty',
            width: '6%',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {currencyFormatter(
                            row['origQty'],
                            DEPTH[futureSymbols[row.symbolId].basePrecision],
                        )}
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'avgPrice',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                return row['priceType'] === PRICE_TYPE.MARKET
                    ? '--'
                    : currencyFormatter(
                          row['price'],
                          DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                      );
            },
        },
        {
            title: t('trigger_price'),
            key: 'exitPrice',
            width: '9%',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['type'] === ORDER_TYPE.CONDITIONAL && row['triggerPrice'] ? (
                            <>
                                {row['triggerPrice'] <= row['price'] ? '<=' : '>='}
                                {currencyFormatter(
                                    row['triggerPrice'],
                                    DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                                )}
                                ({t('last')})
                            </>
                        ) : (
                            '--'
                        )}
                    </span>
                );
            },
        },
        {
            title: t('price_distance'),
            key: 'priceDistance',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                const lastPrice = slowBroker[symbolId] ? Number(slowBroker[symbolId].c) : 0.0;
                const triggerPrice = Number(row['triggerPrice']);
                return (
                    <span>
                        {currencyFormatter(
                            lastPrice,
                            DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="yelloText">
                            (
                            {currencyFormatter(
                                triggerPrice - lastPrice,
                                DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                            )}
                            )
                        </span>
                    </span>
                );
            },
        },
        {
            title: t('trade_type'),
            key: 'tradeType',
            className: 'centerText',
            width: '9%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {t(TRADE_TYPE_LANG_MAP[row['side']])}
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            className: 'centerText',
            width: '6%',
            render: (row: { [x: string]: any }) => {
                return <span>{t(row['priceType'] === PRICE_TYPE.LIMIT ? 'limit' : 'market')}</span>;
            },
        },
        {
            title: t('status'),
            key: 'status',
            className: 'centerText',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return <span>{t(ORDER_STATUS_LANG_MAP[row['status']])}</span>;
            },
        },
        {
            title: t('order_no'),
            key: 'orderNo',
            className: 'centerText',
            width: '12%',
            render: (row: { [x: string]: any }) => {
                return <span>{row['orderId']}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            className: 'centerText',
            width: '11%',
            render: (row: { [x: string]: any }) => {
                return <span>{DateTimeFormatter(Number(row['time']))}</span>;
            },
        },
        {
            title: t('action'),
            key: 'action',
            className: 'centerText',
            width: '12%',
            render: (row: { [x: string]: any }) => {
                return (
                    <FlexContainer styles={{ justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            className="action-button"
                            onClick={() => cancelOrder(row['orderId'], row['type'])}
                        >
                            {t('cancel')}
                        </Button>
                    </FlexContainer>
                );
            },
        },
    ];
    return <StyledTable columns={columns} data={filterConditionalOrders} rowKey="orderId" />;
};

export { ConditionalOrdersTable };
